import { useProctoredAttempt } from '../../components/focus-guard/FocusGuardContext';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useContentProtection } from '../../hooks/useContentProtection';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { useToast } from '../../hooks/use-toast';
import {
  Mic,
  MicOff,
  Play,
  ChevronRight,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Trophy,
  RotateCcw,
  Home,
  Lightbulb,
  Target,
  TrendingUp
} from 'lucide-react';

const ARCQuiz = () => {
  const { chapterId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Content protection
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  useProctoredAttempt({ active: Boolean(quiz) && !showResults, label: 'quiz', onForceEnd: () => completeQuiz() });
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const fetchQuiz = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/arc/chapter/${chapterId}/quiz`);
      setQuiz(response.data);
    } catch (error) {
      console.error('Failed to fetch quiz:', error);
      toast({
        title: 'Error',
        description: 'Failed to load quiz',
        variant: 'destructive'
      });
      navigate(`/arc/chapter/${chapterId}`);
    } finally {
      setLoading(false);
    }
  }, [api, chapterId, navigate, toast]);

  useEffect(() => {
    fetchQuiz();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, [fetchQuiz]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Microphone error:', error);
      toast({
        title: 'Microphone Error',
        description: 'Please allow microphone access to record your answer.',
        variant: 'destructive'
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const submitAnswer = async () => {
    if (!audioBlob || recordingTime < 3) {
      toast({
        title: 'Recording Too Short',
        description: 'Please record at least 3 seconds.',
        variant: 'destructive'
      });
      return;
    }
    
    const currentQuestion = quiz.questions[currentQuestionIndex];
    
    try {
      setSubmitting(true);
      
      // First transcribe the audio
      const formData = new FormData();
      formData.append('audio', audioBlob, 'answer.webm');
      
      const transcribeResponse = await api.post('/arc/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const transcript = transcribeResponse.data.transcript;
      
      // Then submit for evaluation
      const evalResponse = await api.post(
        `/arc/chapter/${chapterId}/quiz/${currentQuestion.id}/answer`,
        { question_id: currentQuestion.id, audio_transcript: transcript }
      );
      
      // Store answer and feedback
      setAnswers(prev => ({
        ...prev,
        [currentQuestion.id]: {
          transcript,
          ...evalResponse.data
        }
      }));
      
      // Move to next question or complete
      if (currentQuestionIndex < quiz.questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
        setAudioBlob(null);
        setRecordingTime(0);
      } else {
        // Complete the quiz
        await completeQuiz();
      }
      
    } catch (error) {
      console.error('Submit error:', error);
      toast({
        title: 'Submission Error',
        description: 'Failed to submit answer. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const completeQuiz = async () => {
    try {
      const response = await api.post(`/arc/chapter/${chapterId}/quiz/complete`);
      setQuizResult(response.data);
      setShowResults(true);
    } catch (error) {
      console.error('Complete quiz error:', error);
      toast({
        title: 'Error',
        description: 'Failed to complete quiz',
        variant: 'destructive'
      });
    }
  };

  const retryQuiz = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setAudioBlob(null);
    setRecordingTime(0);
    setShowResults(false);
    setQuizResult(null);
    fetchQuiz();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-[50vh]" />
        </div>
      </MainLayout>
    );
  }

  if (!quiz) {
    return (
      <MainLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Card className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-bold mb-2">Quiz Not Found</h2>
            <Button onClick={() => navigate(`/arc/chapter/${chapterId}`)}>Return to Chapter</Button>
          </Card>
        </div>
      </MainLayout>
    );
  }

  // Show Results
  if (showResults && quizResult) {
    return (
      <MainLayout>
        <div className="p-6 max-w-4xl mx-auto space-y-6">
          {/* Result Header */}
          <Card className={`${quizResult.passed ? 'bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-emerald-200' : 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200'}`}>
            <CardContent className="pt-8 pb-8 text-center">
              {quizResult.passed ? (
                <Trophy className="h-16 w-16 mx-auto mb-4 text-emerald-600" />
              ) : (
                <Target className="h-16 w-16 mx-auto mb-4 text-amber-600" />
              )}
              <h1 className="text-3xl font-bold mb-2">
                {quizResult.passed ? 'Chapter Complete!' : 'Keep Learning!'}
              </h1>
              <p className="text-muted-foreground mb-4">
                {quizResult.passed 
                  ? 'You have successfully completed this chapter quiz.' 
                  : 'Review the feedback below and try again.'
                }
              </p>
              <div className="flex justify-center gap-8">
                <div>
                  <p className="text-4xl font-bold">{Math.round(quizResult.score)}%</p>
                  <p className="text-sm text-muted-foreground">Your Score</p>
                </div>
                <div>
                  <p className="text-4xl font-bold">{quizResult.passing_score}%</p>
                  <p className="text-sm text-muted-foreground">Passing Score</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Feedback Summary */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              Question Feedback
            </h2>
            
            {quizResult.feedback_summary.map((feedback, index) => (
              <Card key={index} className={feedback.score >= 70 ? 'border-emerald-200' : 'border-amber-200'}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {feedback.score >= 70 ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-amber-600" />
                      )}
                      <span className="font-medium">Question {index + 1}</span>
                    </div>
                    <Badge variant={feedback.score >= 70 ? 'default' : 'secondary'}>
                      {Math.round(feedback.score)}%
                    </Badge>
                  </div>
                  
                  <p className="text-sm mb-3 font-medium">{feedback.question}</p>
                  
                  <div className="bg-muted/50 rounded-lg p-3 mb-3">
                    <p className="text-xs text-muted-foreground mb-1">Your Answer:</p>
                    <p className="text-sm italic">&ldquo;{feedback.your_answer}&rdquo;</p>
                  </div>
                  
                  <p className="text-sm mb-3">{feedback.feedback}</p>
                  
                  {feedback.strengths && feedback.strengths.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs font-semibold text-emerald-600 mb-1">Strengths:</p>
                      <ul className="text-xs space-y-1">
                        {feedback.strengths.map((s, i) => (
                          <li key={i} className="flex items-start gap-1">
                            <CheckCircle2 className="h-3 w-3 text-emerald-500 mt-0.5" />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {feedback.improvements && feedback.improvements.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-amber-600 mb-1">Areas to Improve:</p>
                      <ul className="text-xs space-y-1">
                        {feedback.improvements.map((imp, i) => (
                          <li key={i} className="flex items-start gap-1">
                            <TrendingUp className="h-3 w-3 text-amber-500 mt-0.5" />
                            {imp}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-4 pt-4">
            {!quizResult.passed && quizResult.can_retry && (
              <Button variant="outline" onClick={retryQuiz}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Retry Quiz
              </Button>
            )}
            <Button onClick={() => navigate('/arc')}>
              <Home className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            {quizResult.passed && (
              <Button 
                className="bg-gradient-to-r from-amber-500 to-orange-600"
                onClick={() => navigate('/arc')}
              >
                Continue Learning
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </MainLayout>
    );
  }

  const currentQuestion = quiz.questions[currentQuestionIndex];
  const currentAnswer = answers[currentQuestion?.id];

  return (
    <MainLayout>
      <div 
        className="p-6 max-w-3xl mx-auto space-y-6 select-none"
        style={{ userSelect: 'none' }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Badge variant="outline" className="mb-2">{quiz.chapter_title}</Badge>
            <h1 className="text-xl font-bold">Chapter Quiz</h1>
          </div>
          <Badge className="bg-amber-500">
            {currentQuestionIndex + 1} / {quiz.total_questions}
          </Badge>
        </div>

        {/* Progress */}
        <Progress 
          value={((currentQuestionIndex + 1) / quiz.total_questions) * 100} 
          className="h-2"
        />

        {/* Attempts Info */}
        {quiz.attempts > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertCircle className="h-4 w-4" />
            Attempt {quiz.attempts + 1} of {quiz.max_attempts}
            {quiz.previous_score && ` • Previous best: ${Math.round(quiz.previous_score)}%`}
          </div>
        )}

        {/* Question Card */}
        <Card className="min-h-[50vh]">
          <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
            <CardTitle className="text-lg">
              Question {currentQuestionIndex + 1}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Question */}
            <p className="text-xl font-medium mb-8">{currentQuestion.question}</p>

            {/* Recording Section */}
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  <Mic className="h-4 w-4 inline mr-1" />
                  Record your verbal answer (minimum 3 seconds)
                </p>
                
                <div className="flex flex-col items-center gap-4">
                  {!isRecording && !audioBlob && (
                    <Button
                      size="lg"
                      onClick={startRecording}
                      className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 h-20 w-20 rounded-full"
                    >
                      <Mic className="h-8 w-8" />
                    </Button>
                  )}
                  
                  {isRecording && (
                    <>
                      <Button
                        size="lg"
                        variant="destructive"
                        onClick={stopRecording}
                        className="h-20 w-20 rounded-full animate-pulse"
                      >
                        <MicOff className="h-8 w-8" />
                      </Button>
                      <div className="text-2xl font-mono font-bold text-destructive">
                        {formatTime(recordingTime)}
                      </div>
                      <p className="text-sm text-muted-foreground">Recording... Click to stop</p>
                    </>
                  )}
                  
                  {audioBlob && !isRecording && (
                    <div className="space-y-4 w-full max-w-md">
                      <div className="flex items-center justify-center gap-4 p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg">
                        <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                        <div>
                          <p className="font-medium">Recording Complete</p>
                          <p className="text-sm text-muted-foreground">{formatTime(recordingTime)}</p>
                        </div>
                      </div>
                      
                      <div className="flex justify-center gap-3">
                        <Button variant="outline" onClick={() => { setAudioBlob(null); setRecordingTime(0); }}>
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Re-record
                        </Button>
                        <Button 
                          onClick={submitAnswer}
                          disabled={submitting || recordingTime < 3}
                          className="bg-gradient-to-r from-amber-500 to-orange-600"
                        >
                          {submitting ? (
                            <>
                              <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                              Evaluating...
                            </>
                          ) : (
                            <>
                              Submit Answer
                              <ChevronRight className="h-4 w-4 ml-2" />
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Tips */}
              <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-4">
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-blue-600" />
                  Tips for a Great Answer
                </h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Speak clearly and at a moderate pace</li>
                  <li>• Include specific details and examples</li>
                  <li>• Structure your answer logically</li>
                  <li>• Aim for 30-60 seconds per answer</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ARCQuiz;
